import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import './SegmentDonut.css';

const PALETTE = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#64748b'  // slate
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="segment-donut-tooltip" data-testid="segment-donut-tooltip">
        <p className="tooltip-name">{data.name}</p>
        <p className="tooltip-value" style={{ color: data.payload.fill }}>
          <strong>{data.value?.toLocaleString()}</strong> interactions ({data.payload.percent}%)
        </p>
      </div>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array
};

/**
 * Renders a segmented donut chart for Device, Browser, or Country dimensions.
 */
export default function SegmentDonut({
  title = 'Audience Segment',
  dimension = 'device',
  segments = [],
  metric = 'interactions',
  height = 240
}) {
  const chartData = useMemo(() => {
    if (!Array.isArray(segments) || segments.length === 0) return [];

    const filtered = segments.filter(
      (s) => String(s.dimension).toLowerCase() === String(dimension).toLowerCase()
    );

    const total = filtered.reduce((sum, s) => sum + Number(s[metric] || 0), 0);

    return filtered.map((item, idx) => {
      const val = Number(item[metric] || 0);
      const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
      return {
        name: item.value || 'Other',
        value: val,
        percent: pct,
        fill: PALETTE[idx % PALETTE.length]
      };
    });
  }, [segments, dimension, metric]);

  if (chartData.length === 0) {
    return (
      <div className="segment-donut-card empty" data-testid={`segment-donut-${dimension}-empty`}>
        <h4 className="segment-card-title">{title}</h4>
        <div className="segment-empty-state">No segment data recorded</div>
      </div>
    );
  }

  return (
    <div className="segment-donut-card" data-testid={`segment-donut-${dimension}`}>
      <h4 className="segment-card-title">{title}</h4>
      <div className="segment-chart-wrapper" style={{ height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={75}
              paddingAngle={3}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              iconType="circle"
              formatter={(value) => <span className="donut-legend-item">{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

SegmentDonut.propTypes = {
  title: PropTypes.string,
  dimension: PropTypes.string,
  segments: PropTypes.arrayOf(PropTypes.shape({
    dimension: PropTypes.string,
    value: PropTypes.string,
    interactions: PropTypes.number,
    uniqueSessions: PropTypes.number
  })),
  metric: PropTypes.string,
  height: PropTypes.number
};
